import { h } from 'preact'
import { useReducer, useEffect, useRef, useState } from 'preact/hooks'
import Launcher from './components/Launcher'
import ChatPanel from './components/ChatPanel'
import { chatReducer, initialState } from './state'
import { createChat, loadMessages, sendMessage, postContextNote, sendFeedback } from './api'
import { subscribeTo, unsubscribe } from './cable'
import { saveSession, restoreSession, clearSession, consumeSkipContext, setSkipContext } from './session'
import { describeCurrentPage, gatherPageContext, buildPillLabel, trackCurrentPageView, shouldAnnouncePageChange } from './pageContext'
import { formatMarkdown } from './markdown'
import { bottomBarHeight } from './utils/layout'

// How the launcher invitation behaves. Measured against the numbers in the
// October report: 0.9% of visits open the chat, and six in ten of the people
// who do open it never type.
//
// INVITE_AFTER   wait for the page to settle. Expanding during load competes
//                with the hero image for attention and for main-thread time.
// INVITE_FOR     long enough to read seven words twice, short enough not to
//                sit there nagging.
// SCROLL_TO_HIDE a guest who has started reading has answered the invitation.
//                Any scroll at all would collapse it before a phone user saw
//                it, since scrolling is how they read, so require a deliberate
//                amount.
const INVITE_AFTER = 5000
const INVITE_FOR = 8000
const SCROLL_TO_HIDE = 200
const INVITE_SEEN_KEY = 'kailani_invite_seen'

const MAX_MESSAGES = 30

// phone and phoneTel come from BenningtonContact via the mount element, so the
// number guests see is never hardcoded in the chat.
export default function App({ phone, phoneTel, sms, smsTel, email, redesign = false }) {
  const [state, dispatch] = useReducer(chatReducer, initialState)
  const [searchSuffix, setSearchSuffix] = useState(() => currentSearchParams())
  const [scrollNonce, setScrollNonce] = useState(0)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [launcherOffset, setLauncherOffset] = useState(0)
  const subRef = useRef(null)
  const fallbackRef = useRef(null)
  const toolHideRef = useRef(null)
  // Remembers the property page the chat itself opened, so follow-up context
  // notes can say "I've opened X for you" instead of "I see you're looking at X".
  const chatNavRef = useRef(null)
  const stateRef = useRef(state)
  stateRef.current = state

  // Streaming refs: accumulate content without re-rendering
  const streamBuf = useRef('')
  const streamElRef = useRef(null)
  // Chunk reassembly: chunks carry sequence numbers and can arrive out of
  // order; buffer them and append strictly in sequence.
  const nextSeqRef = useRef(1)
  const pendingChunksRef = useRef(new Map())

  useEffect(() => {
    trackCurrentPageView()
    const saved = restoreSession()
    if (saved) {
      dispatch({
        type: 'RESTORE_SESSION',
        chatId: saved.chatId,
        wasOpen: saved.wasOpen,
        messageCount: saved.messageCount,
        lastSearchTs: saved.lastSearchTs
      })
    }
  }, [])

  useEffect(() => {
    if (state.chatId && state.isOpen && state.messages.length === 0) {
      doLoadMessages(state.chatId)
    }
  }, [state.chatId, state.isOpen])

  useEffect(() => {
    if (state.chatId) {
      saveSession(state)
    }
  }, [state.isOpen, state.chatId, state.messageCount, state.lastSearchTs])

  useEffect(() => {
    if (state.chatId && !subRef.current) {
      subRef.current = subscribeTo(state.chatId, handleStreamData)
    }
    return () => {
      if (subRef.current) {
        unsubscribe(subRef.current)
        subRef.current = null
      }
    }
  }, [state.chatId])

  useEffect(() => {
    let quotePoller = null

    function watchForQuote() {
      if (quotePoller) { clearInterval(quotePoller); quotePoller = null }
      const s = stateRef.current
      if (!s.isOpen || !s.chatId) return
      if (!window.location.pathname.match(/^\/properties\/[a-z0-9-]+\/?$/i)) return


      let attempts = 0
      quotePoller = setInterval(() => {
        attempts++
        // Only a visible fetched quote or a quote_id in the URL counts; the
        // hidden quote_id input exists on every property page for the
        // session's placeholder quote and is not evidence of a real quote.
        const visible = document.querySelector('.quote-success:not(.hidden)')
        const urlQuoteId = new URLSearchParams(window.location.search).get('quote_id')

        if (visible || urlQuoteId) {
          clearInterval(quotePoller)
          quotePoller = null

          detectPageChange({ quoteDetected: true })
        }
        if (attempts >= 15) {
          clearInterval(quotePoller)
          quotePoller = null

        }
      }, 2000)
    }

    function handleTurboLoad() {
      trackCurrentPageView()
      setSearchSuffix(currentSearchParams())
      // Turbo re-attaches the turbo-permanent chat root, which resets the
      // message area's scrollTop to 0. Re-scroll to the latest message.
      setScrollNonce(n => n + 1)
      const s = stateRef.current

      if (s.isOpen && s.chatId) {
        detectPageChange()
        watchForQuote()
      }
    }
    document.addEventListener('turbo:load', handleTurboLoad)
    return () => {
      document.removeEventListener('turbo:load', handleTurboLoad)
      if (quotePoller) clearInterval(quotePoller)
    }
  }, [])

  useEffect(() => {
    return () => {
      if (fallbackRef.current) clearTimeout(fallbackRef.current)
      if (toolHideRef.current) clearTimeout(toolHideRef.current)
    }
  }, [])

  // The launcher shares the bottom-right corner with the mobile booking
  // drawer on property pages, both fixed at z-50. Sit above whatever is there
  // rather than on top of it.
  useEffect(() => {
    function measure() {
      setLauncherOffset(bottomBarHeight(document.querySelector('[data-controller~="booking-drawer"]')))
    }
    measure()
    window.addEventListener('resize', measure)
    document.addEventListener('turbo:load', measure)
    return () => {
      window.removeEventListener('resize', measure)
      document.removeEventListener('turbo:load', measure)
    }
  }, [])

  // Expand the launcher into an invitation once per session, then collapse it.
  // Once per session, not once per page: a guest browsing ten homes should be
  // invited once, not ten times.
  useEffect(() => {
    let seen = false
    try {
      seen = window.sessionStorage.getItem(INVITE_SEEN_KEY) === '1'
    } catch (e) {
      // Private browsing or blocked storage. Showing the invitation again on
      // the next page is a smaller problem than never showing it, so carry on.
    }
    // Nothing to invite someone to who is already here, or already talking.
    // Guests do not get the invitation until :kailani_redesign is on.
    if (!redesign || seen || state.isOpen || state.chatId) return

    // Scrolling means one of two things depending on when it happens, and the
    // same handler covers both: before the invitation appears it cancels it,
    // and afterwards it takes it back. The first version only did the second,
    // so scrolling early disarmed the dismissal and left the invitation
    // stuck open on exactly the pages people scroll.
    let cancelled = false
    let hideTimer = null
    let showTimer = null

    function dismiss() {
      cancelled = true
      if (showTimer) clearTimeout(showTimer)
      if (hideTimer) clearTimeout(hideTimer)
      setInviteOpen(false)
    }

    showTimer = setTimeout(() => {
      if (cancelled || stateRef.current.isOpen) return
      setInviteOpen(true)
      trackEvent('chat_launcher_expanded')
      // Marked seen only once it has actually been shown, so a guest who
      // scrolled past the wait is still invited on the next page.
      try { window.sessionStorage.setItem(INVITE_SEEN_KEY, '1') } catch (e) { /* see above */ }
      hideTimer = setTimeout(() => setInviteOpen(false), INVITE_FOR)
    }, INVITE_AFTER)

    const startedAt = window.scrollY
    function onScroll() {
      if (Math.abs(window.scrollY - startedAt) < SCROLL_TO_HIDE) return
      dismiss()
      window.removeEventListener('scroll', onScroll)
    }
    window.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      clearTimeout(showTimer)
      if (hideTimer) clearTimeout(hideTimer)
      window.removeEventListener('scroll', onScroll)
    }
  }, [state.isOpen, state.chatId, redesign])

  async function doLoadMessages(chatId) {
    try {
      const data = await loadMessages(chatId)
      const msgs = data.messages.map(m => ({
        id: m.id,
        role: m.role,
        content: m.content,
        type: m.message_type === 'context_note' ? 'context_note' : undefined,
        metadata: m.metadata
      }))

      const assistantMsgs = msgs.filter(m => m.role === 'assistant' && m.type !== 'context_note')
      const lastId = assistantMsgs.length > 0 ? assistantMsgs[assistantMsgs.length - 1].id : null

      dispatch({ type: 'SET_MESSAGES', messages: msgs, lastMessageId: lastId })

      if (data.search_update) {
        setTimeout(() => handleSearchUpdate(data.search_update), 600)
      }

      detectPageChange()
    } catch (e) {
      // Silent fail
    }
  }

  function handleStreamData(data) {
    const s = stateRef.current
    switch (data.type) {
      case 'chunk': {
        if (!data.content) break
        // Hide tool activity and typing on first chunk (state update)
        if (streamBuf.current === '' && pendingChunksRef.current.size === 0) {
          dispatch({ type: 'STREAMING_STARTED' })
        }
        // Reassemble in sequence order; unnumbered chunks append directly
        if (data.seq == null) {
          streamBuf.current += data.content
        } else {
          pendingChunksRef.current.set(data.seq, data.content)
          while (pendingChunksRef.current.has(nextSeqRef.current)) {
            streamBuf.current += pendingChunksRef.current.get(nextSeqRef.current)
            pendingChunksRef.current.delete(nextSeqRef.current)
            nextSeqRef.current++
          }
        }
        // Update DOM directly (no re-render)
        if (streamElRef.current) {
          streamElRef.current.innerHTML = formatMarkdown(streamBuf.current)
        }
        // Auto-scroll
        const msgArea = streamElRef.current?.closest('.kailani-messages')
        if (msgArea) {
          requestAnimationFrame(() => { msgArea.scrollTop = msgArea.scrollHeight })
        }
        break
      }

      case 'tool_start':
        dispatch({ type: 'SHOW_TOOL_ACTIVITY', label: data.label || data.tool })
        break

      case 'tool_done':
        handleToolDone()
        break

      case 'retry':
        // Server is retrying the completion; discard the partial stream
        streamBuf.current = ''
        pendingChunksRef.current.clear()
        nextSeqRef.current = 1
        if (streamElRef.current) streamElRef.current.innerHTML = ''
        break

      case 'done': {
        // Flush any straggler chunks (in sequence order) before finalizing
        const stragglers = [...pendingChunksRef.current.entries()].sort((a, b) => a[0] - b[0])
        stragglers.forEach(([, content]) => { streamBuf.current += content })
        pendingChunksRef.current.clear()
        nextSeqRef.current = 1
        // Prefer the server's canonical text over the assembled stream so a
        // lost chunk can never corrupt the final message.
        const finalContent = data.content || streamBuf.current
        streamBuf.current = ''
        dispatch({
          type: 'STREAM_DONE',
          messageId: data.message_id,
          properties: data.properties,
          suggestions: data.suggestions,
          content: finalContent
        })

        if (data.quote_update?.quote_id && data.quote_update?.slug) {
          trackEvent('chat_quote_created', { slug: data.quote_update.slug })
          handleQuoteUpdate(data.quote_update)
        }

        checkForSearchUpdate()
        break
      }

      case 'error':
        streamBuf.current = ''
        pendingChunksRef.current.clear()
        nextSeqRef.current = 1
        dispatch({ type: 'STREAM_ERROR', content: data.content })
        break
    }
  }

  function handleToolDone() {
    const MIN_DISPLAY_MS = 1200
    if (toolHideRef.current) clearTimeout(toolHideRef.current)
    toolHideRef.current = setTimeout(() => {
      dispatch({ type: 'HIDE_TOOL_ACTIVITY' })
    }, MIN_DISPLAY_MS)
  }

  async function checkForSearchUpdate() {
    const s = stateRef.current
    if (!s.chatId) return
    try {
      const data = await loadMessages(s.chatId)
      if (data.search_update) {
        setTimeout(() => handleSearchUpdate(data.search_update), 600)
      }
    } catch (e) {
      // Silent fail
    }
  }

  function handleSearchUpdate(update) {
    const s = stateRef.current
    if (!update || update.timestamp <= s.lastSearchTs) return
    dispatch({ type: 'UPDATE_SEARCH_TS', timestamp: update.timestamp })

    if (update.type === 'search') {
      navigateToSearch(update.ransack_params)
    } else if (update.type === 'property') {
      navigateToProperty(update.slug, update.check_in, update.check_out)
    }
  }

  function navigateToSearch(ransackParams) {
    if (!ransackParams || Object.keys(ransackParams).length === 0) return
    trackEvent('chat_search_navigation')
    const url = new URL('/properties', window.location.origin)
    Object.entries(ransackParams).forEach(([key, value]) => {
      url.searchParams.set(`q[${key}]`, value)
    })
    setSkipContext()
    if (typeof Turbo !== 'undefined') {
      Turbo.visit(url.toString())
    } else {
      window.location.href = url.toString()
    }
  }

  function navigateToProperty(slug, checkIn, checkOut) {
    if (!slug) return
    // Already viewing this property? Only navigate if we have new dates to
    // apply that the URL doesn't carry yet.
    const targetPath = `/properties/${slug}`
    const onTarget = window.location.pathname.replace(/\/$/, '') === targetPath
    const hasDatesAlready = new URLSearchParams(window.location.search).get('q[check_in]')
    if (onTarget && (!checkIn || hasDatesAlready)) return

    dispatch({ type: 'ADD_MESSAGE', message: { type: 'system', content: 'Opening that property for you now...' } })
    setSkipContext()
    // Carry checked dates so the property page auto-builds a quote
    const url = new URL(`/properties/${slug}`, window.location.origin)
    if (checkIn && checkOut) {
      url.searchParams.set('q[check_in]', checkIn)
      url.searchParams.set('q[check_out]', checkOut)
    }
    if (typeof Turbo !== 'undefined') {
      Turbo.visit(url.toString())
    } else {
      window.location.href = url.toString()
    }
  }

  function handleQuoteUpdate(update) {
    const currentPath = window.location.pathname
    const propertyPath = `/properties/${update.slug}`
    const onPropertyPage = currentPath === propertyPath || currentPath === `${propertyPath}/`

    if (onPropertyPage) {
      setSkipContext()
      const url = `${propertyPath}?quote_id=${update.quote_id}`
      setTimeout(() => {
        if (typeof Turbo !== 'undefined') {
          Turbo.visit(url)
        } else {
          window.location.href = url
        }
      }, 1200)
    }
  }

  async function detectPageChange(opts = {}) {
    const s = stateRef.current
    // Nothing to add context to before the conversation has started. See
    // shouldAnnouncePageChange.
    if (!shouldAnnouncePageChange({ isOpen: s.isOpen, chatId: s.chatId, messageCount: s.messages.length })) return

    const current = describeCurrentPage()

    if (consumeSkipContext()) {
      // The chat triggered this navigation. Skip the note (the chat already
      // announced it), but remember the destination so a later note (e.g.
      // when the quote loads) can say the chat opened this page.
      if (current?.slug) {
        chatNavRef.current = { slug: current.slug, at: Date.now() }
      }
      if (!opts.quoteDetected) return
    }

    if (!current) return

    const fromChat = !!(current.slug && chatNavRef.current &&
      chatNavRef.current.slug === current.slug &&
      Date.now() - chatNavRef.current.at < 60000)

    const pillLabel = buildPillLabel(current)

    try {
      const data = await postContextNote(s.chatId, {
        page_type: current.type,
        page_name: current.name || '',
        slug: current.slug || '',
        filters: current.filters || '',
        pill_label: pillLabel,
        has_quote: current.has_quote || false,
        quote_id: current.quote_id || '',
        nav_source: fromChat ? 'chat' : 'user'
      })

      if (data?.id) {
        dispatch({
          type: 'ADD_MESSAGE',
          message: {
            id: data.id,
            type: 'context_note',
            content: data.content,
            metadata: data.metadata
          }
        })
      }
    } catch (e) {
      // Silent fail
    }
  }

  function trackEvent(name, params = {}) {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, params)
    }
  }

  async function handleOpen() {
    dispatch({ type: 'OPEN' })
    setInviteOpen(false)
    trackEvent('chat_open', { from_invitation: inviteOpen })

    const s = stateRef.current
    if (!s.chatId) {
      try {
        const data = await createChat()
        dispatch({ type: 'SET_CHAT_ID', chatId: data.chat_id })
      } catch (e) {
        dispatch({ type: 'ADD_ERROR', content: e.message || 'Unable to start chat. Please try again.' })
      }
    }
  }

  // Start again. A visit already gets its own conversation, but a guest who
  // has wandered off topic mid-visit had no way back to the beginning:
  // clearSession existed and nothing called it.
  function handleReset() {
    if (subRef.current) {
      unsubscribe(subRef.current)
      subRef.current = null
    }
    streamBuf.current = ''
    nextSeqRef.current = 1
    pendingChunksRef.current.clear()
    clearSession()
    dispatch({ type: 'RESET_CONVERSATION' })
    trackEvent('chat_reset')
  }

  function handleClose() {
    dispatch({ type: 'CLOSE' })
  }

  async function handleSend(text) {
    const s = stateRef.current
    if (!text || s.isLoading || !s.chatId) return
    if (s.messageCount >= MAX_MESSAGES) {
      dispatch({
        type: 'ADD_ERROR',
        content: "You've reached the conversation limit. For further assistance, please call us at 888-894-4902 or text 541-705-2267 (Mon-Sat 9am-5pm PST)."
      })
      dispatch({ type: 'DISABLE_INPUT' })
      return
    }

    dispatch({ type: 'ADD_MESSAGE', message: { role: 'user', content: text } })
    dispatch({ type: 'INCREMENT_COUNT' })
    dispatch({ type: 'START_LOADING' })
    trackEvent('chat_message_sent')
    streamBuf.current = ''
    pendingChunksRef.current.clear()
    nextSeqRef.current = 1

    try {
      await sendMessage(s.chatId, text, gatherPageContext())
      startFallbackPoll()
    } catch (e) {
      dispatch({ type: 'STREAM_ERROR', content: e.message || 'Connection error. Please try again.' })
    }
  }

  function handleFeedback(messageId, value) {
    const s = stateRef.current
    if (!s.chatId || !messageId) return
    trackEvent('chat_feedback', { value })
    sendFeedback(s.chatId, messageId, value)
  }

  function handleQuickAction(message) {
    const s = stateRef.current
    if (!s.chatId) {
      handleOpen().then(() => {
        setTimeout(() => handleSend(message), 500)
      })
    } else {
      handleSend(message)
    }
  }

  function startFallbackPoll() {
    if (fallbackRef.current) clearTimeout(fallbackRef.current)

    fallbackRef.current = setTimeout(async () => {
      const s = stateRef.current
      if (s.hasReceivedStreamData || !s.isLoading) return

      let attempts = 0
      const poll = async () => {
        const s2 = stateRef.current
        attempts++
        if (attempts > 30 || !s2.isLoading) return

        try {
          const data = await loadMessages(s2.chatId)
          const chatMessages = data.messages.filter(m => m.message_type !== 'context_note')
          const lastMessage = chatMessages[chatMessages.length - 1]

          if (lastMessage?.role === 'assistant' && String(lastMessage.id) !== String(s2.lastMessageId)) {
            dispatch({
              type: 'STREAM_DONE',
              messageId: lastMessage.id,
              properties: lastMessage.metadata?.property_cards,
              content: lastMessage.content
            })

            if (data.search_update) {
              setTimeout(() => handleSearchUpdate(data.search_update), 600)
            }
            return
          }
        } catch (e) {
          // Continue polling
        }

        setTimeout(poll, 2000)
      }

      poll()
    }, 5000)
  }

  function currentSearchParams() {
    const params = new URLSearchParams(window.location.search)
    const forward = new URLSearchParams()
    const ransackKeys = [
      'q[check_in]', 'q[check_out]', 'q[max_occupancy_gteq]',
      'q[bedrooms_gteq]', 'q[hot_tub]', 'q[pets_friendly_eq]',
      'q[air_conditioning]', 'q[sharc_passes_included]',
      'q[luxury_retreat]', 'q[game_table]', 'q[king_beds]',
      'q[single_floor]', 'q[ev_charger]', 'q[family_loving]',
      'q[wifi]', 'q[is_accessible_eq]', 'q[updated]', 'q[special]',
      'q[caldera_springs]', 'q[price_from]', 'q[price_to]', 'q[review_score_gteq]'
    ]
    const legacyKeys = ['arrival', 'departure', 'guest_count', 'pets', 'promo_code']
    for (const key of [...ransackKeys, ...legacyKeys]) {
      const val = params.get(key)
      if (val) forward.set(key, val)
    }
    const qs = forward.toString()
    return qs ? `?${qs}` : ''
  }

  return (
    <div>
      {!state.isOpen && (
        <Launcher onClick={handleOpen} expanded={inviteOpen} bottomOffset={launcherOffset} />
      )}
      <ChatPanel
        isOpen={state.isOpen}
        messages={state.messages}
        isStreaming={state.isStreaming}
        isLoading={state.isLoading}
        showGreeting={state.showGreeting}
        toolActivity={state.toolActivity}
        messageCount={state.messageCount}
        maxMessages={MAX_MESSAGES}
        inputDisabled={state.inputDisabled}
        onClose={handleClose}
        onReset={handleReset}
        onSend={handleSend}
        onQuickAction={handleQuickAction}
        onFeedback={handleFeedback}
        suggestions={state.suggestions}
        phone={phone}
        phoneTel={phoneTel}
        sms={sms}
        smsTel={smsTel}
        email={email}
        redesign={redesign}
        searchSuffix={searchSuffix}
        streamElRef={streamElRef}
        scrollNonce={scrollNonce}
      />
    </div>
  )
}
